import { useState, useEffect } from 'react'
import { FileText, Search, Filter, Loader2 } from 'lucide-react'
import { Button } from '@/components/Button'
import { DocumentCard } from '@/components/Documents/DocumentCard'
import { LibraryDocumentUploader } from '@/components/Documents/LibraryDocumentUploader'
import { DocumentDetailView } from '@/components/Documents/DocumentDetailView'
import { DocumentsPageSkeleton } from '@/components/Skeleton/DocumentsPageSkeleton'
import type { LibraryDocument, DocumentType } from '@/api/client'
import { api } from '@/api/client'
import { useSubscription } from '@/hooks/useSubscription'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

type FilterStatus = 'all' | 'linked' | 'unlinked'

const DOC_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'Receipt', label: 'Receipt' },
  { value: 'ProofOfPayment', label: 'Proof of Payment' },
  { value: 'LetterOfMedicalNecessity', label: 'LMN' },
  { value: 'ExplanationOfBenefits', label: 'EOB' },
  { value: 'Other', label: 'Other' },
]

export default function DocumentsPage() {
  const navigate = useNavigate()
  const { storageLimitReached } = useSubscription()
  const [documents, setDocuments] = useState<LibraryDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUploader, setShowUploader] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<LibraryDocument | null>(null)

  // Filters
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const [filterType, setFilterType] = useState('')
  const [search, setSearch] = useState('')

  const loadDocuments = async () => {
    setLoading(true)
    setError(null)
    try {
      const params: { linked?: boolean; documentType?: DocumentType; search?: string } = {}
      if (filterStatus === 'linked') params.linked = true
      if (filterStatus === 'unlinked') params.linked = false
      if (filterType) params.documentType = filterType as DocumentType
      if (search) params.search = search

      const docs = await api.getLibraryDocuments(params)
      setDocuments(docs || [])
    } catch (err: any) {
      setError(err?.message || 'Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDocuments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterType, search])

  const handleUploadComplete = (doc: LibraryDocument) => {
    setDocuments(prev => [doc, ...prev])
  }

  const handleDocumentUpdated = (updated: LibraryDocument) => {
    setDocuments(prev => prev.map(d => d.id === updated.id ? updated : d))
    setSelectedDoc(updated)
  }

  const handleDocumentDeleted = (docId: string) => {
    setDocuments(prev => prev.filter(d => d.id !== docId))
    setSelectedDoc(null)
  }

  const handleUploadClick = () => {
    if (storageLimitReached()) {
      toast.error('Storage Limit Reached', {
        description: 'You have reached the maximum storage space for your current plan.',
        action: {
          label: 'Upgrade Plan',
          onClick: () => navigate('/settings?tab=billing')
        }
      });
      return;
    }
    setShowUploader(!showUploader);
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-bold text-[#1B365D]">Documents</h1>
          <p className="text-[14px] text-[#718096] mt-1">
            Upload, preview, and manage your medical expense documentation
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleUploadClick}
        >
          {showUploader ? 'Hide Uploader' : 'Upload Documents'}
        </Button>
      </div>

      {/* Uploader (toggleable) */}
      {showUploader && (
        <div className="mb-6 bg-white rounded-lg border border-[#E2E8F0] p-4">
          <LibraryDocumentUploader onUploadComplete={handleUploadComplete} />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        {/* Status filter */}
        <div className="flex items-center bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          {(['all', 'unlinked', 'linked'] as FilterStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-2 text-[13px] font-medium transition-colors ${filterStatus === status
                  ? 'bg-[#1B365D] text-white'
                  : 'text-[#4A5568] hover:bg-[#F7FAFC]'
                }`}
            >
              {status === 'all' ? 'All' : status === 'unlinked' ? 'Unlinked' : 'Linked'}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-1.5">
          <Filter size={14} className="text-[#A0AEC0]" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-[13px] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#4A5568] bg-white focus:outline-none focus:border-[#1B365D]"
          >
            {DOC_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
          <input
            type="text"
            placeholder="Search by filename..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-[13px] border border-[#E2E8F0] rounded-lg text-[#4A5568] bg-white focus:outline-none focus:border-[#1B365D]"
          />
        </div>

        <div className="text-[12px] text-[#A0AEC0]">
          {documents.length} document{documents.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Document grid */}
      {loading ? (
        <DocumentsPageSkeleton />
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg border border-[#E2E8F0]">
          <p className="text-[14px] text-[#718096] mb-4">{error}</p>
          <Button variant="primary" onClick={loadDocuments}>Retry</Button>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg border border-[#E2E8F0]">
          <FileText size={48} className="mx-auto text-[#A0AEC0] mb-4" />
          <h3 className="text-[16px] font-semibold text-[#1B365D] mb-2">No documents yet</h3>
          <p className="text-[14px] text-[#718096] mb-4">
            Upload your receipts and medical documents to get started
          </p>
          <Button variant="primary" onClick={handleUploadClick}>
            Upload Documents
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {documents.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onClick={() => setSelectedDoc(doc)}
            />
          ))}
        </div>
      )}

      {/* Detail view modal */}
      {selectedDoc && (
        <DocumentDetailView
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onDocumentUpdated={handleDocumentUpdated}
          onDocumentDeleted={handleDocumentDeleted}
        />
      )}
    </div>
  )
}
